import { useEffect, useState } from "react";
import { Button, Card, Modal, Select, Input, message } from "antd";
import {
  DownloadOutlined,
  SendOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  FileZipOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  RequestZipExport,
  GetZipExportStatus,
  SendZipUrlToOrganizers,
} from "../../../../../apis/events";
import { getCountryDialCodes } from "../../../../../apis/organizers";
import { ZipExportStatusRender } from "../../../../../components/common/StatusRender";

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return "0 KB";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

// Data retention policy — "hand user-uploaded photos to the client" flow.
// The zip itself is built by a background cron (CronController::
// generateUserUploadedPhotosZip), not by this component — this only
// requests it, polls status manually (no polling convention exists
// elsewhere in this app, so a manual Refresh button is used instead of a
// timer), and lets an admin WhatsApp the finished zip's URL to one or more
// organizer numbers once it's ready.
export const PhotoHandoverCard = ({ id, user }) => {
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [status, setStatus] = useState("not_requested");
  const [zipUrl, setZipUrl] = useState(null);
  const [sentLog, setSentLog] = useState([]);
  const [hasUserUploadedPhotos, setHasUserUploadedPhotos] = useState(null);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [uploadedByUsers, setUploadedByUsers] = useState(0);
  const [zipPhotoCount, setZipPhotoCount] = useState(null);
  const [zipSizeBytes, setZipSizeBytes] = useState(null);
  const [zipGeneratedAt, setZipGeneratedAt] = useState(null);

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [numbers, setNumbers] = useState([{ code: "+91", mobile: "" }]);
  const [sending, setSending] = useState(false);
  const [dialCodeOptions, setDialCodeOptions] = useState([]);
  const [dialCodesLoading, setDialCodesLoading] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await GetZipExportStatus({ id });
      if (res.status === 200 && res.data?.success) {
        setStatus(res.data.status);
        setZipUrl(res.data.zip_url);
        setSentLog(res.data.sent_log || []);
        setHasUserUploadedPhotos(!!res.data.has_user_uploaded_photos);
        setTotalPhotos(res.data.total_photos || 0);
        setUploadedByUsers(res.data.uploaded_by_users || 0);
        setZipPhotoCount(res.data.zip_photo_count ?? null);
        setZipSizeBytes(res.data.zip_size_bytes ?? null);
        setZipGeneratedAt(res.data.zip_generated_at || null);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDialCodes = async () => {
    if (dialCodesLoading || dialCodeOptions.length > 0) return;
    setDialCodesLoading(true);
    try {
      const res = await getCountryDialCodes();
      if (res?.status === 200 && res?.data) {
        const countries = res.data?.data;
        if (Array.isArray(countries)) {
          const seen = new Set();
          const mapped = [];
          for (const item of countries) {
            const codeRaw = item?.dial_code;
            const code = String(codeRaw).startsWith("+") ? String(codeRaw) : `+${String(codeRaw)}`;
            if (!seen.has(code)) {
              seen.add(code);
              mapped.push({ label: `${item?.flag ? item.flag + " " : ""}${code} ${item?.name}`.trim(), value: code });
            }
          }
          if (mapped.length > 0) setDialCodeOptions(mapped);
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setDialCodesLoading(false);
    }
  };

  const handleGenerateZip = async () => {
    setRequesting(true);
    try {
      const res = await RequestZipExport({ id });
      if (res.status === 200 && res.data?.success) {
        message.success("Zip export requested — it will be ready shortly.");
        setStatus("generating");
      } else {
        message.error(res.data?.message || "Failed to request zip export.");
      }
    } catch (error) {
      message.error("Failed to request zip export.");
    } finally {
      setRequesting(false);
    }
  };

  const alreadySentKeys = sentLog.map((e) => `${e.country_code}${e.mobile}`);

  const updateNumberRow = (index, field, value) => {
    setNumbers((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addNumberRow = () => setNumbers((prev) => [...prev, { code: "+91", mobile: "" }]);
  const removeNumberRow = (index) => setNumbers((prev) => prev.filter((_, i) => i !== index));

  const handleSendUrl = async () => {
    const payload = numbers
      .filter((n) => n.mobile && n.mobile.trim() !== "")
      .map((n) => ({ country_code: n.code.replace("+", ""), mobile: n.mobile.trim() }));

    if (payload.length === 0) {
      message.warning("Add at least one mobile number.");
      return;
    }

    setSending(true);
    try {
      const res = await SendZipUrlToOrganizers({ id, numbers: payload });
      if (res.status === 200 && res.data?.success) {
        message.success(`Sent to ${res.data.sent?.length || 0} number(s).`);
        setSentLog(res.data.sent_log || []);
        setNumbers([{ code: "+91", mobile: "" }]);
        setSendModalOpen(false);
      } else {
        message.error(res.data?.message || "Failed to send.");
      }
    } catch (error) {
      message.error("Failed to send.");
    } finally {
      setSending(false);
    }
  };

  if (user?.user_type !== "admin") {
    return null;
  }

  if (!hasUserUploadedPhotos) {
    return null;
  }

  const newPhotosSinceZip =
    status === "ready" && zipPhotoCount != null ? totalPhotos - zipPhotoCount : 0;

  return (
    <Card
      className="my-3"
      title={
        <>
          <FileZipOutlined className="me-2" />
          User Uploaded Photo Handover
        </>
      }
      extra={<ZipExportStatusRender value={status} />}
    >
      <p className="text-muted mb-0" style={{ fontSize: 12 }}>
        Download every photo attendees uploaded for this event as one ZIP file.
      </p>

      <div className="d-flex gap-3 mt-3 flex-wrap">
        <div className="p-2 flex-fill" style={{ background: "#fafafa", borderRadius: 6, minWidth: 140 }}>
          <p className="text-muted mb-0" style={{ fontSize: 12 }}>Total photos</p>
          <p className="fw-bold mb-0" style={{ fontSize: 20 }}>{totalPhotos}</p>
        </div>
        <div className="p-2 flex-fill" style={{ background: "#fafafa", borderRadius: 6, minWidth: 140 }}>
          <p className="text-muted mb-0" style={{ fontSize: 12 }}>Uploaded by</p>
          <p className="fw-bold mb-0" style={{ fontSize: 20 }}>
            {uploadedByUsers} <span style={{ fontSize: 12, fontWeight: 400 }}>users</span>
          </p>
        </div>
      </div>

      {status === "not_requested" && (
        <p className="mt-3 mb-0" style={{ fontSize: 13, color: "#888" }}>
          No ZIP created yet. It will include all {totalPhotos} photos.
        </p>
      )}

      {status === "generating" && (
        <p className="mt-3 mb-0" style={{ fontSize: 13, color: "#888" }}>
          ZIP is being generated in the background. Refresh to check status.
        </p>
      )}

      {status === "no_photos" && (
        <p className="mt-3 mb-0" style={{ fontSize: 13, color: "#888" }}>
          This event has no user-uploaded photos, so no zip was created.
        </p>
      )}

      {status === "ready" && (
        <>
          <p className="mt-3 mb-0" style={{ fontSize: 13, color: "#888" }}>
            ZIP generated on {zipGeneratedAt ? dayjs(zipGeneratedAt).format("DD MMM YYYY, h:mm A") : "-"}
            {" · "}
            {zipPhotoCount ?? 0} photos{" · "}
            {formatBytes(zipSizeBytes)}
          </p>
          {newPhotosSinceZip > 0 && (
            <p className="mb-0" style={{ fontSize: 13, color: "#f5a623" }}>
              {newPhotosSinceZip} new photo{newPhotosSinceZip > 1 ? "s" : ""} uploaded since then. Regenerate to
              include them.
            </p>
          )}
        </>
      )}

      <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
        <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchStatus}>
          Refresh
        </Button>

        <Button
          icon={<DownloadOutlined />}
          loading={requesting}
          disabled={status === "generating"}
          onClick={handleGenerateZip}
        >
          {status === "generating" ? "Generating..." : status === "ready" ? "Regenerate" : "Generate ZIP"}
        </Button>

        {status === "ready" && zipUrl && (
          <Button type="primary" icon={<DownloadOutlined />} href={zipUrl} target="_blank" rel="noopener noreferrer">
            Download ZIP
          </Button>
        )}

        {status === "ready" && (
          <Button
            icon={<SendOutlined />}
            onClick={() => {
              fetchDialCodes();
              setSendModalOpen(true);
            }}
          >
            Send to Organizers
          </Button>
        )}
      </div>

      {sentLog.length > 0 && (
        <div className="mt-3">
          <p className="mb-1" style={{ fontSize: 13, color: "#888" }}>Already sent to:</p>
          {sentLog.map((entry, i) => (
            <span key={i} className="badge bg-dark me-2 mb-1">
              +{entry.country_code} {entry.mobile}
            </span>
          ))}
        </div>
      )}

      <Modal
        title="Send Zip URL to Organizers"
        open={sendModalOpen}
        onCancel={() => setSendModalOpen(false)}
        onOk={handleSendUrl}
        confirmLoading={sending}
        okText="Send URL"
      >
        {numbers.map((row, index) => {
          const key = `${row.code.replace("+", "")}${row.mobile}`;
          const isAlreadySent = alreadySentKeys.includes(key);
          return (
            <div key={index} className="d-flex align-items-center gap-2 mb-2">
              <Select
                showSearch
                placeholder="Code"
                value={row.code}
                onChange={(val) => updateNumberRow(index, "code", val)}
                options={dialCodeOptions}
                optionFilterProp="label"
                optionLabelProp="label"
                loading={dialCodesLoading}
                style={{ width: "35%" }}
              />
              <Input
                placeholder="Mobile Number"
                value={row.mobile}
                onChange={(e) => updateNumberRow(index, "mobile", e.target.value.replace(/[^0-9]/g, ""))}
                style={{ width: "45%" }}
                status={isAlreadySent ? "warning" : ""}
              />
              {numbers.length > 1 && (
                <Button icon={<DeleteOutlined />} onClick={() => removeNumberRow(index)} />
              )}
              {isAlreadySent && (
                <span style={{ fontSize: 11, color: "#f5a623" }}>Already sent</span>
              )}
            </div>
          );
        })}
        <Button icon={<PlusOutlined />} type="dashed" block onClick={addNumberRow}>
          Add Another
        </Button>
      </Modal>
    </Card>
  );
};
